import { ChangeDetectionStrategy, Component, Input, computed, signal } from '@angular/core';

/**
 * Host-agnostic stage indicator. Renders one of the four metamorphosis
 * stages (egg / caterpillar / chrysalis / butterfly) as an inline SVG.
 * The paths mirror the originals at apps/kirigami-app/public/stage-*.svg
 * so the component is self-contained and works without asset wiring.
 *
 * Usage:
 *   <kirigami-stage-icon stage="caterpillar" [size]="32"></kirigami-stage-icon>
 */
export type KirigamiStage = 'egg' | 'caterpillar' | 'chrysalis' | 'butterfly';

@Component({
  selector: 'kirigami-stage-icon',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host {
        display: inline-flex;
        line-height: 0;
      }
      svg {
        display: block;
      }
    `,
  ],
  template: `
    @switch (stage) {
      @case ('egg') {
        <svg
          [attr.width]="size"
          [attr.height]="eggHeight()"
          viewBox="0 0 29 42"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          [attr.aria-label]="resolvedAriaLabel()"
          role="img"
        >
          <path
            fill="#B5AED7"
            d="M14.347.39c3.606 0 7.313 4.267 9.969 9.671l-10.137 6.277-9.985-6.183C6.744 4.793 10.41.391 14.347.391Z"
          />
          <path
            fill="#998CC6"
            d="M24.662 10.781c1.23 2.639 2.205 5.507 2.8 8.26.1.433.189.892.268 1.377.2 1.168.325 2.305.365 3.38l-13.531 8.38V17.033l10.098-6.253ZM13.768 17.019V32.16L.6 24.007c.016-.344.035-.68.058-1.005.033-.625.098-1.281.192-1.96.131-1.028.303-1.945.513-2.764a40.498 40.498 0 0 1 2.497-7.395l9.91 6.136Z"
          />
          <path
            fill="#7460A1"
            d="M28.123 24.718c.02.588.031 1.204.031 1.846 0 8.231-6.065 14.918-13.59 15.044v-8.495l13.559-8.395ZM13.769 33.096v8.501C6.413 41.266.543 34.663.543 26.565c0-.569.008-1.117.023-1.644l13.203 8.175Z"
          />
        </svg>
      }
      @case ('caterpillar') {
        <svg
          [attr.width]="size"
          [attr.height]="size"
          viewBox="0 0 49 48"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          [attr.aria-label]="resolvedAriaLabel()"
          role="img"
        >
          <path fill="#BA4872" d="M4.636 29.57v8.176L.5 32.942l1.717-3.372h2.418Z" />
          <path
            fill="#CD6590"
            d="M13.132 28.424v9.958l-7.699-.16v-8.844l2.26-2.704h4.199l1.24 1.75Z"
          />
          <path
            fill="#DC8AAE"
            d="M22.356 38.51V26.604l-5.535-.535-2.963 2.305v9.957l4.427 2.563 4.071-2.386Z"
          />
          <path
            fill="#CD6590"
            d="m33.75 34.152-7.534-9.338-3.063 1.67v11.898l5.409 2.513 5.188-6.743Z"
          />
          <path
            fill="#BA4872"
            d="m34.346 33.69-7.538-9.361 1.712-3.132 10.823 3.34.317 6.274-5.314 2.878Z"
          />
          <path
            fill="#CD6590"
            d="m39.663 23.789-10.917-3.377 1.238-5.695 5.424-3.022 6.438 9.476-2.183 2.618Z"
          />
          <path
            fill="#DC8AAE"
            d="m36.857 10.81 4.066-2.452.492.815-3.674 2.215h5.522l.001.001c5.637-.156 6.776 6.418 3.155 8.029l-.1.038c-.005.002-.012.002-.017.003v.01L42.61 20.84l-5.957-8.686-3.234-4.494.772-.557 2.666 3.705Z"
          />
        </svg>
      }
      @case ('chrysalis') {
        <svg
          [attr.width]="size"
          [attr.height]="size"
          viewBox="0 0 49 48"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          [attr.aria-label]="resolvedAriaLabel()"
          role="img"
        >
          <path
            fill="#F5B8AC"
            d="M35.71 19.684H13.29l-2.747-3.544 12.36-9.917h3.193l12.361 9.917-2.747 3.544Z"
          />
          <path
            fill="#F8A898"
            d="M34.456 30.272H14.578l-2.795-3.225 1.473-6.245h22.421l1.541 6.245-2.762 3.225Z"
          />
          <path
            fill="#FA907C"
            d="M33.528 38.291H15.474l-1.03-2.795v-4.082h20.113v4.082l-1.03 2.795Z"
          />
          <path
            fill="#E8755E"
            d="M15.62 39.156H33.38l-3.069 4.316-6.323 3.1-5.533-3.1-2.836-4.316Z"
          />
          <path fill="#F5B8AC" d="M26.097 5.103h-3.192l-3.941-3.675h11.074l-3.941 3.675Z" />
        </svg>
      }
      @case ('butterfly') {
        <svg
          [attr.width]="size"
          [attr.height]="size"
          viewBox="0 0 49 48"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          [attr.aria-label]="resolvedAriaLabel()"
          role="img"
        >
          <path fill="#59A88F" d="m23.27 16.311 5.959-6.091.489.5-5.96 6.091z" />
          <path
            fill="#59A88F"
            d="m25.195 16.812-5.96-6.091.49-.5 5.96 6.092zM13.41 13.514H3.795L.5 16.848 9.51 26.4l4.658-.376-.758-12.51ZM35.589 13.514h9.615l3.295 3.334-9.01 9.553-4.778-.282.878-12.605Z"
          />
          <path
            fill="#72BDA3"
            d="m15.17 25.98 4.965-2.381 3.85-1.88-9.616-8.204.802 12.465ZM33.936 26.074l-5.598-2.653-3.324-1.702 9.615-8.203-.693 12.558Z"
          />
          <path
            fill="#59A88F"
            d="m23.989 36.001-8.925-8.656-2.731 2.878V43.43l3.967-4.917h5.442L23.989 36ZM24.988 36.001l8.925-8.656 2.73 2.878V43.43l-3.966-4.917h-5.442L24.988 36ZM24.5 15.035l3.185 3.256-3.185 2.661-3.185-2.661 3.185-3.256Z"
          />
          <path
            fill="#72BDA3"
            d="M33.403 26.805 24.5 35.254l-.003-.003V22.336l.002-.001 8.904 4.47Z"
          />
          <path fill="#C6E6DA" d="m15.593 26.805 8.903 8.449.001-.001V22.335l-8.904 4.47Z" />
        </svg>
      }
    }
  `,
})
export class KirigamiStageIcon {
  readonly #stage = signal<KirigamiStage>('egg');
  readonly #size = signal<number>(32);
  readonly #ariaLabel = signal<string>('');

  /** Which stage to render. */
  @Input({ required: true }) set stage(value: KirigamiStage) {
    this.#stage.set(value);
  }
  get stage(): KirigamiStage {
    return this.#stage();
  }

  /** Pixel width; height equals `size` for caterpillar/chrysalis/butterfly. */
  @Input() set size(value: number) {
    this.#size.set(value);
  }
  get size(): number {
    return this.#size();
  }

  /** Accessible label. Falls back to the stage name. */
  @Input() set ariaLabel(value: string) {
    this.#ariaLabel.set(value);
  }

  /** Egg keeps the 29:42 intrinsic ratio. */
  readonly eggHeight = computed(() => Math.round((this.#size() * 42) / 29));

  readonly resolvedAriaLabel = computed(() => this.#ariaLabel() || `${this.#stage()} stage`);
}
